import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ApplicationPathConstants, ERROR_SEVERITY, ErrorDCR } from '@ddt-web/config';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { UrlQueryParamService } from '@ddt-web/url';
import { first } from 'rxjs/operators';

import { PopupDialogObject, PopupDialogOptions, PopupErrorObject } from '../models';
import { PopupDialogAction, PopupDialogActionOptions } from '../models/popup-dialog-object';
import { AbstractErrorHandlerService } from './abstract-error-handler.service';
import { ErrorHelperService } from './error-helper.service';
import { ErrorPopupService } from './error-popup.service';

@Injectable({
	providedIn: 'root'
})
export class PortfolioAnalysisValidationErrorHandlerService extends AbstractErrorHandlerService {

	private readonly errorsToMonitor = [
		'min2Holdings',
		'weightOverMax',
		'min3HoldingsCash',
		'duplicateMorningstarFundId'
	];

	private allowOtherErrors: boolean = true;

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService,
		protected urlService: UrlQueryParamService
	) {
		super(contentService, errorPopupService, tealiumAnalyticsService, errorHelperService);
	}

	public handleError(error: any): void {
		const isPortfolioEditor = this.errorHelperService.getRouterUrl().includes(ApplicationPathConstants.PORTFOLIO_EDITOR_PATH);
		if (!isPortfolioEditor && error instanceof PopupErrorObject && this.errorsToMonitor.includes(error.responseJson?.errorCode)) {
			this.trackErrorEvent();

			const portfolioId = error.responseJson.id;
			const actions = [];
			if (!PortfolioIdUtil.isExternalPortfolio(portfolioId)) {
				actions.push(
					PopupDialogAction.create({
						label: this.contentService.get('ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL', ErrorDCR.ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL),
						type: 'view_portfolio_editor',
						reload: false,
						portfolioId
					} as PopupDialogActionOptions)
				);
			}

			if (error.responseJson?.mode === SharedApplicationConstants.DIALOG_TABLE_MODE.COMPARE && PortfolioIdUtil.isExternalPortfolio(portfolioId)) {
				// Show cancel button if user is on analysis page and tries to compare with a partner portfolio 
				actions.push(PopupDialogAction.create({
					label: this.contentService.get('ERROR_CANCEL_LABEL', ErrorDCR.ERROR_CANCEL_LABEL),
					type: 'continue',
					reload: false,
					actionClass: actions.length ? 'btn-secondary' : 'btn-primary'
				} as PopupDialogActionOptions));
			} else {
				// New analysis button
				actions.push(PopupDialogAction.create({
					label: this.contentService.get('ERROR_NEW_ANALYSIS_LABEL', ErrorDCR.ERROR_NEW_ANALYSIS_LABEL),
					type: 'view_new_analysis',
					reload: false,
					actionClass: actions.length ? 'btn-secondary' : 'btn-primary'
				} as PopupDialogActionOptions));
			}

			const failedMessage = PopupDialogObject.create({
				success: false,
				title: this.contentService.get('ANALYSIS_VALIDATION_ERROR_TITLE', 'Error with one of your portfolios'),
				description: this.contentService.get(error.responseJson.errorText.key, error.responseJson.errorText.text),
				actions,
				listings: [
					{ iconType: 'times', description: error.responseJson?.description }
				]
			} as PopupDialogOptions);

			this.allowOtherErrors = false;

			const dialogRef = this.errorPopupService.openResponseMessageDialog(failedMessage, true, undefined, undefined, true);
			dialogRef.afterClosed$.pipe(first()).subscribe(() => {
				this.allowOtherErrors = true;
			});
		} else if (this.allowOtherErrors) {
			this.nextHandler.handleError(error);
		}
	}

	private trackErrorEvent() {
		this.errorSeverity = ERROR_SEVERITY.ERROR;
		this.trackErrorMessage = 'portfolioAnalysisValidationError';
		this.tealiumAnalyticsService.trackError(this.trackErrorMessage, this.errorSeverity);
	}
}
